import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Grow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Custom Dialog Component for Data Setup Pages
 * Consistent styling with modern gradient effects
 */
const CustomDialog = ({
  open,
  onClose,
  title,
  subtitle,
  iconSrc,
  children,
  onSubmit,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  maxWidth = "md",
  fullWidth = true,
  fullScreen = false,
  loading = false,
  hideActions = false,
  disableSubmit = false,
  mobileFullScreen = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const effectiveFullScreen = fullScreen || (mobileFullScreen && isMobile);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={effectiveFullScreen ? false : maxWidth}
      fullWidth={fullWidth}
      fullScreen={effectiveFullScreen}
      TransitionComponent={Grow}
      transitionDuration={{
        enter: 400,
        exit: 300,
      }}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.paper,
          borderRadius: effectiveFullScreen ? 0 : { xs: 2.5, sm: 3.5 },
          maxHeight: effectiveFullScreen ? "100vh" : { xs: "96vh", sm: "90vh" },
          height: effectiveFullScreen ? "100vh" : "auto",
          width: effectiveFullScreen
            ? "100vw"
            : fullWidth
              ? "100%"
              : "auto",
          margin: effectiveFullScreen ? 0 : { xs: 1, sm: "auto" },
          border: effectiveFullScreen
            ? "none"
            : `1px solid ${theme.palette.divider}`,
          boxShadow: effectiveFullScreen
            ? "none"
            : "0 22px 58px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(8, 15, 30, 0.45)",
          backdropFilter: "blur(8px)",
          transition: "all 0.4s ease-in-out",
        },
      }}
    >
      {/* Header with Icon */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2.25 },
          minHeight: effectiveFullScreen ? "auto" : { xs: 84, sm: 100 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.25, sm: 2 },
          background: `linear-gradient(130deg, ${theme.palette.primary.main}14 0%, ${theme.palette.secondary.main}10 52%, ${theme.palette.background.paper} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          flexShrink: 0,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)",
          },
        }}
      >
        {/* Icon Container */}
        {iconSrc && (
          <Box
            component="img"
            src={iconSrc}
            alt={`${title} Icon`}
            sx={{
              width: { xs: 38, sm: 48 },
              height: { xs: 38, sm: 48 },
              minWidth: { xs: 38, sm: 48 },
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.9,
              position: "relative",
              zIndex: 1,
            }}
          />
        )}

        {/* Title and Subtitle */}
        <Box
          sx={{
            flexGrow: 1,
            pr: 1,
            position: "relative",
            zIndex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: subtitle ? 0.5 : 0,
              letterSpacing: 0.2,
              lineHeight: 1.25,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              wordBreak: "break-word",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                lineHeight: 1.35,
                wordBreak: "break-word",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            ml: 0.25,
            color: theme.palette.text.secondary,
            position: "relative",
            zIndex: 1,
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            "&:hover": {
              backgroundColor: `${theme.palette.primary.main}14`,
              borderColor: `${theme.palette.primary.main}66`,
              color: theme.palette.primary.main,
            },
            transition: "all 0.2s ease",
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.75 },
          flexGrow: 1,
          overflowY: "auto",
          background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.background.default,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: 3,
            "&:hover": {
              backgroundColor: theme.palette.text.disabled,
            },
          },
        }}
      >
        {children}
      </Box>

      {/* Footer with Actions */}
      {!hideActions && (
        <>
          <Divider />
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.75, sm: 2.25 },
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              backgroundColor: theme.palette.background.paper,
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              flexDirection: { xs: "column-reverse", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                py: { xs: 1.1, sm: 1.2 },
                px: 3,
                width: { xs: "100%", sm: "auto" },
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                fontSize: "0.95rem",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}10`,
                },
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={loading || disableSubmit}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                py: { xs: 1.1, sm: 1.2 },
                px: 3,
                width: { xs: "100%", sm: "auto" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: "#ffffff",
                fontSize: "0.95rem",
                letterSpacing: 0.5,
                boxShadow: "0 10px 24px rgba(31, 41, 55, 0.24)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 28px rgba(31, 41, 55, 0.3)",
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                },
                "&:active": {
                  transform: "translateY(0px)",
                },
                "&:disabled": {
                  opacity: 0.6,
                },
              }}
            >
              {submitLabel}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default CustomDialog;
